import {
  Alert,
  Button,
  Card,
  CardBody,
  CategoryBreakdown,
  DatePicker,
  EmptyState,
  FormField,
  formatCurrency,
  formatDate,
  Inline,
  Money,
  SegmentedControl,
  Skeleton,
  Stack,
  StatCard,
  StatStrip,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableLoading,
  TableRow,
  Toolbar,
  ToolbarCount,
  VisuallyHidden,
  type BreakdownRow,
} from '@hkdevs/design-system';
import {
  useCategories,
  useChecks,
  type BreakdownScope,
  type CategoryBreakdown as Breakdown,
  type PayCheck,
} from '../../api/queries/pay';

/** Pay History's URL state (ADR 0023). The default scope, latest, is left out. */
export interface PayHistorySearch {
  scope?: Exclude<BreakdownScope, 'latest'> | undefined;
  /** The range's first and last pay dates, ISO. */
  from?: string | undefined;
  to?: string | undefined;
}

const SCOPES: { value: BreakdownScope; label: string }[] = [
  { value: 'all', label: 'All time' },
  { value: 'latest', label: 'Latest payment' },
  { value: 'range', label: 'Date range' },
];

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** Validates the search params: anything unknown or malformed is dropped. */
export function parsePayHistorySearch(search: Record<string, unknown>): PayHistorySearch {
  const text = (value: unknown) => (typeof value === 'string' ? value : undefined);
  const date = (value: unknown) => {
    const iso = text(value);
    return iso && ISO_DATE.test(iso) ? iso : undefined;
  };
  const scope = text(search.scope);
  return {
    scope: scope === 'all' || scope === 'range' ? scope : undefined,
    from: date(search.from),
    to: date(search.to),
  };
}

/** An ISO date as DatePicker's local-midnight Date, and back. */
export const toDate = (iso: string) => {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  return new Date(y, m - 1, d);
};
export const toIso = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

/** The default range: the 14 days ending at the last pay date. */
export function defaultRange(lastPayDate: string): { from: string; to: string } {
  const start = toDate(lastPayDate);
  start.setDate(start.getDate() - 13);
  return { from: toIso(start), to: lastPayDate };
}

/** "12/24/2022 – 01/06/2023". */
export function formatRange(from: string, to: string) {
  return `${formatDate(from)} – ${formatDate(to)}`;
}

const dollars = (cents: number) => (cents / 100).toFixed(2);

/** The API's breakdown as CategoryBreakdown's rows: dollars, deductions negative. */
function rows(breakdown: Breakdown | undefined): {
  earnings: BreakdownRow[];
  deductions: BreakdownRow[];
} {
  return {
    earnings: (breakdown?.earnings ?? []).map((c) => ({
      label: c.label,
      amount: dollars(c.amountCents),
    })),
    deductions: (breakdown?.deductions ?? []).map((c) => ({
      label: c.label,
      amount: dollars(-c.amountCents),
    })),
  };
}

export interface PayHistoryViewProps {
  /** A supervised rep's pay; the caller's own when left out. */
  repId?: string;
  search: PayHistorySearch;
  /** Replaces the URL state with these values (undefined removes one). */
  onSearchChange: (next: PayHistorySearch) => void;
  onOpenCheck: (payDate: string) => void;
}

/**
 * A rep's Pay History page body (pc-dbb.2), from GET /api/pay/checks: the summary, when
 * the tenant shows it (A15), and the checks. The settings arrive with the checks.
 */
export function PayHistoryView({
  repId,
  search,
  onSearchChange,
  onOpenCheck,
}: PayHistoryViewProps) {
  const checks = useChecks(repId);

  if (checks.isPending) return <PayHistoryLoading />;
  if (checks.isError) {
    return (
      <Alert
        tone="danger"
        title="Pay history couldn’t be loaded"
        action={
          <Button size="sm" variant="secondary" onClick={() => void checks.refetch()}>
            Retry
          </Button>
        }
      >
        Check your connection and try again.
      </Alert>
    );
  }
  const { items, display } = checks.data;
  if (items.length === 0) {
    return (
      <Card>
        <CardBody>
          <EmptyState
            title="No pay history yet"
            description="Processed pay will appear here once it’s issued."
            icon="credit-card"
            headingLevel={2}
          />
        </CardBody>
      </Card>
    );
  }

  return (
    <>
      {display.summary && (
        <PayHistorySummary
          repId={repId}
          lastPayDate={items[0]!.payDate}
          search={search}
          onSearchChange={onSearchChange}
          showBreakdown={display.breakdown}
        />
      )}
      <Checks checks={items} onOpenCheck={onOpenCheck} />
    </>
  );
}

interface PayHistorySummaryProps {
  repId: string | undefined;
  /** The newest check's pay date: the default range ends there. */
  lastPayDate: string;
  search: PayHistorySearch;
  onSearchChange: (next: PayHistorySearch) => void;
  /** The category breakdown under the stat strip (A15). */
  showBreakdown: boolean;
}

/**
 * The summary (A15): the scope selector and its dates, the stat strip and, when shown,
 * the category breakdown. It owns the categories query and the scope/from/to URL state,
 * so with the summary off neither is used.
 */
function PayHistorySummary({
  repId,
  lastPayDate,
  search,
  onSearchChange,
  showBreakdown,
}: PayHistorySummaryProps) {
  const scope: BreakdownScope = search.scope ?? 'latest';
  const fallback = defaultRange(lastPayDate);
  const from = search.from ?? fallback.from;
  const to = search.to ?? fallback.to;
  const ready = scope !== 'range' || from <= to;
  const categories = useCategories({ repId, scope, from, to });

  const breakdown = ready ? categories.data : undefined;
  const { earnings, deductions } = rows(breakdown);
  const scopeLabel =
    scope === 'all'
      ? 'All time'
      : scope === 'latest'
        ? 'Latest payment'
        : ready
          ? formatRange(from, to)
          : 'Select a date range';
  const amountOf = (list: readonly BreakdownRow[], label: string) =>
    formatCurrency(Math.abs(Number(list.find((row) => row.label === label)?.amount ?? 0)));
  const setRange = (next: { from?: string | undefined; to?: string | undefined }) =>
    onSearchChange({ ...search, scope: 'range', from, to, ...next });

  let figures;
  if (categories.isError) {
    figures = (
      <Alert
        tone="danger"
        title="The summary couldn’t be loaded"
        action={
          <Button size="sm" variant="secondary" onClick={() => void categories.refetch()}>
            Retry
          </Button>
        }
      >
        Check your connection and try again. The checks are below.
      </Alert>
    );
  } else if (ready && categories.isLoading) {
    figures = <SummaryLoading showBreakdown={showBreakdown} />;
  } else {
    figures = (
      <>
        <StatStrip>
          <StatCard
            label="Earnings"
            value={
              <Money value={dollars(breakdown?.totalEarningsCents ?? 0)} tone="positive" signed />
            }
            hint={scopeLabel}
            tone="success"
          />
          <StatCard
            label="Deductions"
            value={
              <Money value={dollars(-(breakdown?.totalDeductionsCents ?? 0))} tone="negative" />
            }
            hint={scopeLabel}
            tone="danger"
          />
          <StatCard
            label="Total"
            value={<Money value={dollars(breakdown?.takeHomeCents ?? 0)} tone="neutral" />}
            hint={scopeLabel}
          />
        </StatStrip>

        {showBreakdown && (
          <CategoryBreakdown
            title={`Category breakdown · ${scope === 'range' && !ready ? 'Date range' : scopeLabel}`}
            total={dollars(breakdown?.takeHomeCents ?? 0)}
            earnings={earnings}
            deductions={deductions}
            pairTooltip={(label) =>
              `${label} appears in both columns: ${amountOf(earnings, label)} earned and ${amountOf(deductions, label)} adjusted.`
            }
          />
        )}
      </>
    );
  }

  return (
    <>
      <Stack gap={3}>
        <Toolbar aria-label="Summary scope">
          <SegmentedControl
            aria-label="Summary scope"
            size="sm"
            options={SCOPES}
            value={scope}
            onChange={(value) =>
              onSearchChange(
                value === 'range'
                  ? { ...search, scope: 'range', from, to }
                  : {
                      ...search,
                      scope: value === 'all' ? 'all' : undefined,
                      from: undefined,
                      to: undefined,
                    },
              )
            }
          />
          <ToolbarCount>Showing: {scopeLabel}</ToolbarCount>
        </Toolbar>
        {scope === 'range' && (
          <>
            <Inline gap={4} rowGap={3} align="end">
              <FormField label="Start date">
                <DatePicker
                  size="sm"
                  allowTextInput
                  value={from ? toDate(from) : null}
                  {...(to ? { max: toDate(to) } : {})}
                  onChange={(start) => setRange({ from: start ? toIso(start) : undefined })}
                />
              </FormField>
              <FormField label="End date">
                <DatePicker
                  size="sm"
                  allowTextInput
                  value={to ? toDate(to) : null}
                  {...(from ? { min: toDate(from) } : {})}
                  onChange={(end) => setRange({ to: end ? toIso(end) : undefined })}
                />
              </FormField>
            </Inline>
            {!ready && (
              <Alert tone="info">
                Select a start and end date to filter the summary and breakdown below.
              </Alert>
            )}
          </>
        )}
      </Stack>
      {figures}
    </>
  );
}

/** The processed checks, newest first; a row opens its check. */
function Checks({
  checks,
  onOpenCheck,
}: {
  checks: readonly PayCheck[];
  onOpenCheck: (payDate: string) => void;
}) {
  return (
    <Card flush>
      <Table aria-label="Checks">
        <TableHead>
          <TableCell>Pay Date</TableCell>
          <TableCell numeric>Amount</TableCell>
          <TableCell width="44px">
            <VisuallyHidden>Open</VisuallyHidden>
          </TableCell>
        </TableHead>
        <TableBody>
          {checks.map((check) => (
            <TableRow
              key={check.payDate}
              label={`Open the check for ${formatDate(check.payDate)}`}
              onClick={() => onOpenCheck(check.payDate)}
            >
              <TableCell rowHeader tone="strong">
                {formatDate(check.payDate)}
              </TableCell>
              <TableCell numeric tone="strong">
                <Money value={dollars(check.amountCents)} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

/** The summary's placeholders while its categories load. */
function SummaryLoading({ showBreakdown }: { showBreakdown: boolean }) {
  return (
    <>
      <StatStrip aria-hidden="true">
        {['Earnings', 'Deductions', 'Total'].map((label) => (
          <StatCard key={label} label={label} value={<Skeleton width="60%" />} />
        ))}
      </StatStrip>
      {showBreakdown && (
        <Card aria-hidden="true">
          <CardBody>
            <Stack gap={3}>
              <Skeleton width="40%" />
              <Skeleton />
              <Skeleton />
              <Skeleton width="75%" />
            </Stack>
          </CardBody>
        </Card>
      )}
      <VisuallyHidden role="status">Loading the summary</VisuallyHidden>
    </>
  );
}

/**
 * The checks' placeholder while they load. The pay history settings arrive with the
 * checks, so the summary's shape isn't known yet: it isn't drawn (A15).
 */
function PayHistoryLoading() {
  return (
    <Card flush>
      <Table aria-label="Checks" busy>
        <TableHead>
          <TableCell>Pay Date</TableCell>
          <TableCell numeric>Amount</TableCell>
        </TableHead>
        <TableBody>
          <TableLoading colSpan={2} rows={4} label="Loading pay history" />
        </TableBody>
      </Table>
    </Card>
  );
}
